"use client";

import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * GlobalRevealGuard
 * ------------------------------------------------------------
 * Sab pages ke [data-reveal] elements ko ek hi jagah se handle karta hai.
 *
 *  - JS chalne se pehle (ya JS fail ho to) html par `reveal-armed` class nahi
 *    hoti -> reveal-guard.css sab kuch visible rakhti hai.
 *  - JS chalne par: viewport ke andar / upar wale elements turant `is-visible`
 *    ho jate hain, neeche wale scroll par reveal hote hain.
 *  - Reload / anchor-jump / bfcache / client-side route change / late-rendered
 *    content -> sab handle hota hai.
 *  - 4s ka safety net: kuch bhi atka ho to sab dikha deta hai.
 *
 * Agar kisi page ka class naam alag ho (e.g. "reveal", "fade-up"),
 * to neeche SELECTOR / VISIBLE_CLASS change karo (aur CSS me bhi same selector).
 */

const SELECTOR = "[data-reveal]";
const VISIBLE_CLASS = "is-visible";
const ARMED_CLASS = "reveal-armed";
const SAFETY_MS = 4000;

const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function GlobalRevealGuard() {
  const pathname = usePathname();

  useIsoLayoutEffect(() => {
    const html = document.documentElement;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      // Armed nahi karenge -> CSS sab kuch visible rakhegi
      html.classList.remove(ARMED_CLASS);
      return;
    }

    const pending = new Set<HTMLElement>();

    const reveal = (el: HTMLElement) => {
      el.classList.add(VISIBLE_CLASS);
      pending.delete(el);
      io.unobserve(el);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          // Dikh gaya YA user scroll / anchor-jump karke isse cross kar gaya
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
            reveal(el);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -40px 0px" }
    );

    const handle = (el: HTMLElement) => {
      if (el.classList.contains(VISIBLE_CLASS) || pending.has(el)) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (el.getBoundingClientRect().top < vh * 0.92) {
        el.classList.add(VISIBLE_CLASS); // fold ke andar/upar -> hide hi mat karo
      } else {
        pending.add(el);
        io.observe(el);
      }
    };

    const scan = (root: ParentNode) => {
      if (root instanceof HTMLElement && root.matches(SELECTOR)) handle(root);
      root.querySelectorAll<HTMLElement>(SELECTOR).forEach(handle);
    };

    // 1) pehle in-view items ko visible mark karo, 2) phir arm karo (same tick,
    //    paint se pehle) -> koi flash nahi
    scan(document);
    html.classList.add(ARMED_CLASS);

    // Late-rendered content (fetch ke baad, Suspense, client navigation, etc.)
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) =>
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) scan(node);
        })
      );
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Back/forward cache se page wapas aaye
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) scan(document);
    };
    window.addEventListener("pageshow", onPageShow);

    // Last safety net
    const safety = window.setTimeout(() => {
      Array.from(pending).forEach(reveal);
    }, SAFETY_MS);

    return () => {
      window.clearTimeout(safety);
      window.removeEventListener("pageshow", onPageShow);
      mo.disconnect();
      io.disconnect();
    };
  }, [pathname]);

  return null;
}
